/**
 * Booking hours helpers — extracted from `BookingWidget.jsx` to keep that file
 * focused on rendering. These helpers translate a vendor's "operating hours"
 * config (which can be a single window, full 24h, or a 7-day per-day schedule)
 * into a list of selectable pickup/return time strings for the date-aware
 * Select inputs in the widget.
 */

// 30-min granularity over a typical operating window. Stored & sent as "HH:MM";
// the parent merges date+time into a single ISO timestamp.
export const ALL_TIME_OPTIONS = (() => {
  const out = [];
  for (let h = 0; h <= 23; h++) {
    for (const m of [0, 30]) {
      out.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return out;
})();

function filterByWindow(openStr, closeStr) {
  const toMin = (s) => { const [h, m] = s.split(":").map(Number); return h * 60 + m; };
  const op = toMin(openStr);
  const cl = toMin(closeStr);
  const wraps = cl < op;
  return ALL_TIME_OPTIONS.filter((t) => {
    const tm = toMin(t);
    return wraps ? (tm >= op || tm <= cl) : (tm >= op && tm <= cl);
  });
}

const DEFAULT_DAY_WINDOW = ALL_TIME_OPTIONS.filter((t) => {
  const [h] = t.split(":").map(Number);
  return h >= 7 && h <= 22;
});

/**
 * Filter ALL_TIME_OPTIONS down to the vendor's operating window.
 * - vendor.business_hours_per_day (7 entries Mon=0..Sun=6) overrides everything when set;
 *   if `dateForDay` is provided, we pick that weekday's window.
 * - 24h vendors: full 48-slot list.
 * - Vendor with no hours set: familiar 07:00–22:30 default.
 * - Vendor with explicit single window: only inside-window slots. Overnight (close < open) wraps.
 */
export function timeOptionsFor(vendor, dateForDay) {
  if (!vendor) return DEFAULT_DAY_WINDOW;

  const perDay = Array.isArray(vendor.business_hours_per_day) && vendor.business_hours_per_day.length === 7
    ? vendor.business_hours_per_day : null;
  if (perDay && dateForDay) {
    // JS Date.getDay(): 0=Sunday..6=Saturday. We want 0=Monday..6=Sunday.
    const d = new Date(dateForDay);
    if (isNaN(d)) return ALL_TIME_OPTIONS;
    const jsDay = d.getDay();
    const idx = (jsDay + 6) % 7;
    const day = perDay[idx] || {};
    if (day.is_closed) return [];
    if (day.is_24h) return ALL_TIME_OPTIONS;
    if (!day.open_time || !day.close_time) return ALL_TIME_OPTIONS;
    return filterByWindow(day.open_time, day.close_time);
  }
  if (vendor.business_24h) return ALL_TIME_OPTIONS;
  if (!vendor.business_open_time || !vendor.business_close_time) {
    return DEFAULT_DAY_WINDOW;
  }
  return filterByWindow(vendor.business_open_time, vendor.business_close_time);
}

const WEEKDAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

/**
 * Friendly label of the vendor's hours, weekday-aware when per-day is set.
 * Returns null when no hours are configured (caller hides the hint).
 */
export function hoursLabelFor(vendor, dateForDay) {
  if (!vendor) return null;
  const perDay = Array.isArray(vendor.business_hours_per_day) && vendor.business_hours_per_day.length === 7
    ? vendor.business_hours_per_day : null;
  if (perDay) {
    if (!dateForDay) return "different hours per day";
    const d = new Date(dateForDay);
    if (isNaN(d)) return null;
    const jsDay = d.getDay();
    const idx = (jsDay + 6) % 7;
    const day = perDay[idx] || {};
    const dayName = WEEKDAY_NAMES[idx];
    if (day.is_closed) return `${dayName} — closed`;
    if (day.is_24h) return `${dayName} — 24h`;
    if (day.open_time && day.close_time) return `${dayName} ${day.open_time}–${day.close_time}`;
    return null;
  }
  if (vendor.business_24h || !vendor.business_open_time) return null;
  return `${vendor.business_open_time}–${vendor.business_close_time}`;
}
